/**
 * AvailableTasksList Component
 *
 * Renders the server-backed, lazily-paginated list of pickable tasks for the
 * Related Tasks panel. The list scrolls inside its own `AppScrollArea`; the
 * captured viewport is handed to `InfiniteScrollSection` as the observer `root`
 * so infinite-scroll fires on this container's scroll, not the page viewport
 * (the SlidePanel content clips it).
 *
 * Reuses `TaskResultCard` (the same selection card the old virtualized selector
 * rendered) and the shared `InfiniteScrollSection` / `EmptyState`.
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/RelatedTasksListPanel/core
 */

import { useState } from 'react';
import { Stack, Text, Center, Loader } from '@mantine/core';
import {
  IconClipboardOff,
  IconAlertTriangle,
  IconSearchOff,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/common/EmptyState';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { InfiniteScrollSection } from '@/components/ui/PaginatedList';
import type { TaskListItem } from '@/types';
import { TaskResultCard } from '../../components/TaskResultCard';
import styles from './AvailableTasksList.module.css';

/** Empty-state icon size — matches EmptyState's own `size="sm"` icon scale. */
const EMPTY_STATE_ICON_SIZE = 32;

interface AvailableTasksListProps {
  tasks: TaskListItem[];
  isLoading: boolean;
  hasLoaded: boolean;
  hasNextPage: boolean;
  error: string | null;
  hasActiveFilters: boolean;
  onTaskSelect: (taskId: string) => void;
  onLoadMore: () => void;
}

export function AvailableTasksList({
  tasks,
  isLoading,
  hasLoaded,
  hasNextPage,
  error,
  hasActiveFilters,
  onTaskSelect,
  onLoadMore,
}: AvailableTasksListProps) {
  const { t } = useTranslation();

  // DOM node of the scrollable viewport — used as the IntersectionObserver root
  // so infinite-scroll fires on this list's scroll, not the page.
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);

  // Initial load in flight — show a spinner instead of an empty/error flash.
  if (isLoading && tasks.length === 0 && !error) {
    return (
      <Center className={styles.stateCenter}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (error && tasks.length === 0) {
    return (
      <EmptyState
        icon={<IconAlertTriangle size={EMPTY_STATE_ICON_SIZE} />}
        title={t('tasks.loadError')}
        description={t('tasks.loadErrorDescription')}
        action={{
          label: t('common.retry'),
          onClick: onLoadMore,
          variant: 'outline',
        }}
        size="sm"
      />
    );
  }

  // Only show the terminal empty state when the server is exhausted. If more
  // pages exist (a full page was filtered down to zero visible rows), fall
  // through to render the list + InfiniteScrollSection so loadMore can fire.
  if (tasks.length === 0 && hasLoaded && !hasNextPage) {
    return hasActiveFilters ? (
      <EmptyState
        icon={<IconSearchOff size={EMPTY_STATE_ICON_SIZE} />}
        title={t('wizard.step4.noFilteredResults')}
        size="sm"
      />
    ) : (
      <EmptyState
        icon={<IconClipboardOff size={EMPTY_STATE_ICON_SIZE} />}
        title={t('wizard.step4.noTasksAvailable')}
        size="sm"
      />
    );
  }

  return (
    <Stack gap="xs" className={styles.container}>
      <Text size="sm" fw={500} className={styles.label}>
        {t('wizard.step4.availableTasks')}
      </Text>

      <div className={styles.scrollWrapper}>
        <AppScrollArea fill viewportRef={setViewport}>
          <Stack gap="sm" className={styles.list}>
            {tasks.map((task) => (
              <TaskResultCard
                key={task.id}
                task={task}
                onClick={() => onTaskSelect(task.id)}
              />
            ))}

            <InfiniteScrollSection
              hasNextPage={hasNextPage}
              isLoading={isLoading}
              onLoadMore={onLoadMore}
              root={viewport}
            />
          </Stack>
        </AppScrollArea>
      </div>
    </Stack>
  );
}
